import React from 'react';
import ReactECharts from 'echarts-for-react';
import ChartCard from '../ui/ChartCard';

const DashboardCharts = ({ data }) => {
  const { learners, phishingSimulations, complianceRecords, assignments = [], auditLogs = [] } = data;

  const deptCompletionOption = () => {
    const depts = [...new Set(learners.map(l => l.department).filter(Boolean))];
    const chartData = depts.map(dept => {
      const dl = learners.filter(l => l.department === dept);
      if (dl.length === 0) return 0;
      
      let completedLearners = 0;
      let assignedLearnersCount = 0;

      dl.forEach(learner => {
        const learnerAssignments = assignments.filter(a => String(a.employee_id) === String(learner.id));
        if (learnerAssignments.length > 0) {
          assignedLearnersCount++;
          if (learnerAssignments.every(a => a.status === 'Completed')) {
            completedLearners++;
          }
        }
      });

      if (assignedLearnersCount === 0) return 0;
      return Math.round((completedLearners / assignedLearnersCount) * 100);
    });

    return {
      tooltip: { trigger: 'axis', formatter: '{b}: {c}%' },
      grid: { left: '3%', right: '5%', top: '5%', bottom: '5%', containLabel: true },
      xAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%', fontSize: 11 }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
      yAxis: { type: 'category', data: depts, axisLabel: { fontSize: 11 } },
      series: [{ type: 'bar', data: chartData.map(v => ({ value: v, itemStyle: { color: v >= 75 ? '#10b981' : v >= 50 ? '#3b82f6' : '#f59e0b', borderRadius: [0, 4, 4, 0] } })), barMaxWidth: 16 }],
    };
  };

  // Uses phishing_simulations table exactly as requested
  const phishingTrendOption = () => {
    const sortedSims = [...phishingSimulations].sort((a, b) => new Date(a.date) - new Date(b.date));
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Click Rate %', 'Report Rate %'], bottom: 0, textStyle: { fontSize: 11 } },
      grid: { left: '3%', right: '4%', bottom: '18%', top: '5%', containLabel: true },
      xAxis: { type: 'category', data: sortedSims.map(s => (s.name || '').split(':')[0]?.trim()), axisLabel: { fontSize: 10, rotate: 8 } },
      yAxis: { type: 'value', axisLabel: { formatter: '{value}%', fontSize: 10 }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
      series: [
        { name: 'Click Rate %', type: 'line', data: sortedSims.map(s => s.sent > 0 ? ((s.clicked || 0) / s.sent * 100).toFixed(1) : 0), itemStyle: { color: '#ef4444' }, areaStyle: { color: 'rgba(239,68,68,0.06)' }, smooth: true },
        { name: 'Report Rate %', type: 'line', data: sortedSims.map(s => s.sent > 0 ? ((s.reported || 0) / s.sent * 100).toFixed(1) : 0), itemStyle: { color: '#10b981' }, areaStyle: { color: 'rgba(16,185,129,0.06)' }, smooth: true },
      ],
    };
  };

  const compliancePieOption = () => {
    const statuses = ['Compliant', 'In Progress', 'At Risk', 'Overdue', 'Not Started'];
    const counts = statuses.map(s => complianceRecords.filter(r => r.status === s).length);
    const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#94a3b8'];
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      series: [{
        type: 'pie', radius: ['48%', '72%'],
        data: statuses.map((s, i) => ({ name: s, value: counts[i], itemStyle: { color: colors[i] } })),
        label: { show: false }, emphasis: { label: { show: true, fontSize: 12, fontWeight: 'bold' } },
      }],
    };
  };

  // Follows risk score computation logic dynamically from audit tables and assignments
  const riskScoreOption = () => {
    const depts = [...new Set(learners.map(l => l.department).filter(Boolean))];
    const chartData = depts.map(dept => {
      const dl = learners.filter(l => l.department === dept);
      if (dl.length === 0) return 0;
      
      const deptRisk = dl.reduce((acc, learner) => {
        let score = 20; // Base score
        const lLogs = auditLogs.filter(a => String(a.user_id) === String(learner.id));
        
        const clicks = lLogs.filter(a => a.action === 'Phishing Click' || a.action === 'click').length;
        const submissions = lLogs.filter(a => a.action === 'Credential Submission' || a.action === 'submission').length;
        const reports = lLogs.filter(a => a.action === 'Phishing Report' || a.action === 'report').length;
        
        score += (clicks * 15);
        score += (submissions * 25);
        score -= (reports * 5);

        const lAssignments = assignments.filter(a => String(a.employee_id) === String(learner.id));
        const overdue = lAssignments.filter(a => a.status === 'Overdue').length;
        score += (overdue * 10);
        
        score = Math.max(0, Math.min(100, score));
        return acc + score;
      }, 0);

      const avg = Math.round(deptRisk / dl.length);
      return { value: avg, itemStyle: { color: avg < 20 ? '#10b981' : avg < 50 ? '#f59e0b' : '#ef4444', borderRadius: [0, 4, 4, 0] } };
    });

    return {
      tooltip: { trigger: 'axis' },
      grid: { left: '3%', right: '5%', top: '5%', bottom: '5%', containLabel: true },
      xAxis: { type: 'value', max: 100, splitLine: { lineStyle: { color: '#f1f5f9' } }, axisLabel: { fontSize: 10 } },
      yAxis: { type: 'category', data: depts, axisLabel: { fontSize: 11 } },
      series: [{
        type: 'bar', barMaxWidth: 16,
        data: chartData,
      }],
    };
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <ChartCard title="Training" subtitle="Completion by department">
        <ReactECharts option={deptCompletionOption()} style={{ height: 200 }} />
      </ChartCard>
      <ChartCard title="Phishing" subtitle="Simulation performance over time">
        <ReactECharts option={phishingTrendOption()} style={{ height: 200 }} />
      </ChartCard>
      <ChartCard title="Compliance" subtitle="Status of all learners">
        <div className="flex items-center gap-6">
          <ReactECharts option={compliancePieOption()} style={{ height: 180, flex: 1 }} />
          <div className="space-y-2 flex-shrink-0">
            {[['Compliant', '#10b981'], ['In Progress', '#3b82f6'], ['At Risk', '#f59e0b'], ['Overdue', '#ef4444'], ['Not Started', '#94a3b8']].map(([label, color]) => (
              <div key={label} className="flex items-center gap-2 text-xs text-slate-600">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                {label}
              </div>
            ))}
          </div>
        </div>
      </ChartCard>
      <ChartCard title="Risk Score" subtitle="Learners by avg. risk level">
        <ReactECharts option={riskScoreOption()} style={{ height: 200 }} />
      </ChartCard>
    </div>
  );
};

export default DashboardCharts;